import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit3, MessageCircle, Save, X, Check, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import './ProposalEditor.css';

const ProposalEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [edits, setEdits] = useState([]);
  const [editMode, setEditMode] = useState('ai_comment'); // 'ai_comment' or 'manual_edit'
  const [selectedText, setSelectedText] = useState('');
  const [comment, setComment] = useState('');
  const [manualEdit, setManualEdit] = useState('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [applyingChanges, setApplyingChanges] = useState(false);
  const [previewingChanges, setPreviewingChanges] = useState(false);
  const [confirmingChanges, setConfirmingChanges] = useState(false);
  const [loadingProposal, setLoadingProposal] = useState(true);
  const [previewContent, setPreviewContent] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const proposalRef = useRef(null);

  useEffect(() => {
    loadProposal();
    loadEdits();
  }, [id]);

  const loadProposal = async () => {
    setLoadingProposal(true);
    try {
      const response = await apiService.getProposalContent(id);
      console.log('Proposal data received:', response.data);
      setProposal(response.data);
    } catch (error) {
      toast.error('Failed to load proposal');
      console.error('Error loading proposal:', error);
    } finally {
      setLoadingProposal(false);
    }
  };

  const loadEdits = async () => {
    try {
      const response = await apiService.getProposalEdits(id);
      const newEdits = response.data.edits || [];
      setEdits(newEdits);
      
      // Check if there are pending edits
      const pendingEdits = newEdits.filter(edit => edit.status === 'pending');
      setHasChanges(pendingEdits.length > 0);
      
      // If we have pending edits and no preview content, auto-preview
      if (pendingEdits.length > 0 && !previewContent) {
        previewChanges();
      }
    } catch (error) {
      console.error('Error loading edits:', error);
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection.toString().trim()) {
      setSelectedText(selection.toString().trim());
      if (editMode === 'ai_comment') {
        setShowCommentModal(true);
      } else {
        setShowManualModal(true);
      }
    }
  };

  const addAIComment = async () => {
    if (!comment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      const editData = {
        type: 'ai_comment',
        text_selection: selectedText,
        comment: comment,
        position: { x: 0, y: 0 } // Could be enhanced with actual position
      };

      await apiService.addProposalEdit(id, editData);
      setComment('');
      setSelectedText('');
      setShowCommentModal(false);
      loadEdits(); // Reload edits
      toast.success('AI comment added');
    } catch (error) {
      toast.error('Failed to add AI comment');
      console.error('Error adding AI comment:', error);
    }
  };

  const addManualEdit = async () => {
    if (!manualEdit.trim()) {
      toast.error('Please enter the new text');
      return;
    }

    try {
      const editData = {
        type: 'manual_edit',
        text_selection: selectedText,
        manual_edit: manualEdit,
        position: { x: 0, y: 0 } // Could be enhanced with actual position
      };

      await apiService.addProposalEdit(id, editData);
      setManualEdit('');
      setSelectedText('');
      setShowManualModal(false);
      loadEdits(); // Reload edits
      toast.success('Manual edit added');
    } catch (error) {
      toast.error('Failed to add manual edit');
      console.error('Error adding manual edit:', error);
    }
  };

  const previewChanges = async () => {
    const pendingEdits = edits.filter(edit => edit.status === 'pending');
    
    if (pendingEdits.length === 0) {
      toast('No pending changes to preview', { icon: 'ℹ️' });
      return;
    }

    setPreviewingChanges(true);
    toast.loading(`Previewing ${pendingEdits.length} edits with AI...`, { duration: 0 });
    
    try {
      const response = await apiService.previewEdits(id);
      
      // Dismiss the loading toast
      toast.dismiss();
      
      // Check if there was an error in the response
      if (response.data.error) {
        toast.error(`❌ ${response.data.message}`);
        console.error('AI processing error:', response.data.details);
        return;
      }
      
      // Success case - update preview content
      setPreviewContent(response.data.updated_content);
      toast.success(`✅ Preview updated with ${pendingEdits.length} edits`);
      
    } catch (error) {
      toast.dismiss();
      toast.error('❌ Failed to preview changes');
      console.error('Error previewing changes:', error);
    } finally {
      setPreviewingChanges(false);
    }
  };

  const confirmChanges = async () => {
    const pendingEdits = edits.filter(edit => edit.status === 'pending');
    
    if (pendingEdits.length === 0) {
      toast('No pending changes to confirm', { icon: 'ℹ️' });
      return;
    }

    if (!previewContent) {
      toast.error('No preview content available. Please preview changes first.');
      return;
    }

    setConfirmingChanges(true);
    toast.loading(`Saving ${pendingEdits.length} edits...`, { duration: 0 });
    
    try {
      const response = await apiService.applyAllEdits(id, { previewed_content: previewContent });
      
      // Dismiss the loading toast
      toast.dismiss();
      
      // Check if there was an error in the response
      if (response.data.error) {
        toast.error(`❌ ${response.data.message}`);
        console.error('Save error:', response.data.details);
        return;
      }
      
      // Success case
      toast.success(`✅ ${response.data.message}`);
      
      // If a new proposal was created, show info about it
      if (response.data.new_proposal_id) {
        toast.success(`📄 New proposal saved as: ${response.data.new_proposal_id}`);
      }
      
      // Navigate back to proposals view
      navigate('/proposals');
      
    } catch (error) {
      toast.dismiss();
      toast.error('❌ Failed to save changes');
      console.error('Error saving changes:', error);
    } finally {
      setConfirmingChanges(false);
    }
  };

  const applyAllChanges = async () => {
    // Legacy function - now redirects to confirm
    await confirmChanges();
  };

  const deleteEdit = async (editId) => {
    try {
      // Note: This would need a delete endpoint in the backend
      // For now, we'll just remove it from the local state
      setEdits(edits.filter(edit => edit.id !== editId));
      toast.success('Edit removed');
    } catch (error) {
      toast.error('Failed to remove edit');
    }
  };

  const getEditStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-900/30 text-yellow-300 border border-yellow-700';
      case 'applied': return 'bg-green-900/30 text-green-300 border border-green-700';
      case 'rejected': return 'bg-red-900/30 text-red-300 border border-red-700';
      default: return 'bg-slate-700 text-slate-100';
    }
  };

  if (loadingProposal || !proposal) {
    return (
      <div className="proposal-editor-container">
        <div className="loading">
          <div className="spinner"></div>
          <span>Loading proposal content...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="proposal-editor-container">
      {/* Header */}
      <div className="editor-header">
        <button onClick={() => navigate('/proposals')} className="back-button">
          <ArrowLeft className="w-4 h-4" />
          Back to Proposals
        </button>
        <h1 className="editor-title">Edit Proposal: {proposal.proposal?.client_name || 'Unknown'}</h1>
      </div>

      <div className="editor-content">
        {/* Edit Controls */}
        <div className="edit-controls">
          <div className="edit-mode-selector">
            <button
              onClick={() => setEditMode('ai_comment')}
              className={`mode-button ${editMode === 'ai_comment' ? 'active' : ''}`}
            >
              <MessageCircle className="w-4 h-4" />
              AI Comments
            </button>
            <button
              onClick={() => setEditMode('manual_edit')}
              className={`mode-button ${editMode === 'manual_edit' ? 'active' : ''}`}
            >
              <Edit3 className="w-4 h-4" />
              Manual Edits
            </button>
          </div>

          <div className="edit-instructions">
            {editMode === 'ai_comment' ? (
              <p>Select text and add AI comments. Preview changes live, then confirm to save.</p>
            ) : (
              <p>Select text and provide the new text. Preview changes live, then confirm to save.</p>
            )}
          </div>

          <div className="action-buttons">
            <button
              onClick={previewChanges}
              disabled={previewingChanges || !hasChanges}
              className={`preview-button ${previewingChanges ? 'previewing' : ''}`}
            >
              {previewingChanges ? (
                <>
                  <div className="spinner"></div>
                  <span>Previewing...</span>
                </>
              ) : (
                <>
                  <MessageCircle className="w-4 h-4" />
                  <span>Preview Changes</span>
                </>
              )}
            </button>

            <button
              onClick={confirmChanges}
              disabled={confirmingChanges || !hasChanges || !previewContent}
              className={`confirm-button ${confirmingChanges ? 'confirming' : ''}`}
            >
              {confirmingChanges ? (
                <>
                  <div className="spinner"></div>
                  <span>Confirming...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Confirm & Save</span>
                </>
              )}
            </button>
          </div>
        </div>

        <div className="editor-main">
          {/* Proposal Content */}
          <div className="proposal-content" ref={proposalRef} onMouseUp={handleTextSelection}>
            <h2>
              {previewContent ? 'Preview Content' : 'Proposal Content'}
              {previewContent && (
                <span className="preview-badge">Preview Mode</span>
              )}
            </h2>
            
            {previewContent ? (
              // Show preview content
              <div className="preview-content">
                <div 
                  className="slide-text"
                  dangerouslySetInnerHTML={{ __html: previewContent }}
                />
              </div>
            ) : (
              // Show original content
              proposal.slides?.length > 0 ? (
                proposal.slides.map((slide, index) => (
                  <div key={index} className="slide-content">
                    <h3>Slide {slide.slide_number}: {slide.title}</h3>
                    <div 
                      className="slide-text"
                      dangerouslySetInnerHTML={{ __html: slide.content }}
                    />
                  </div>
                ))
              ) : (
                <div className="slide-content">
                  <p style={{color: '#f9fafb'}}>
                    {proposal ? 'No slides found in proposal data' : 'Loading proposal...'}
                  </p>
                  {proposal && (
                    <pre style={{color: '#9ca3af', fontSize: '12px', whiteSpace: 'pre-wrap'}}>
                      {JSON.stringify(proposal, null, 2)}
                    </pre>
                  )}
                </div>
              )
            )}
          </div>

          {/* Edits Panel */}
          <div className="edits-panel">
            <h3>Pending Changes ({edits.filter(e => e.status === 'pending').length})</h3>
            
            {edits.length === 0 ? (
              <p className="no-edits">No edits yet. Select text to add changes.</p>
            ) : (
              <div className="edits-list">
                {edits.map((edit) => (
                  <div key={edit.id} className={`edit-item ${edit.status}`}>
                    <div className="edit-header">
                      <span className={`edit-type ${edit.type}`}>
                        {edit.type === 'ai_comment' ? 'AI Comment' : 'Manual Edit'}
                      </span>
                      <span className={`edit-status ${getEditStatusColor(edit.status)}`}>
                        {edit.status}
                      </span>
                      <button
                        onClick={() => deleteEdit(edit.id)}
                        className="delete-edit"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <div className="edit-content">
                      <div className="selected-text">
                        <strong>Selected:</strong> "{edit.text_selection}"
                      </div>
                      
                      {edit.type === 'ai_comment' ? (
                        <div className="comment">
                          <strong>Comment:</strong> {edit.comment}
                        </div>
                      ) : (
                        <div className="manual-edit">
                          <strong>New Text:</strong> {edit.manual_edit}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Comment Modal */}
      {showCommentModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add AI Comment</h3>
              <button onClick={() => setShowCommentModal(false)} className="close-button">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="modal-content">
              <div className="selected-text-display">
                <strong>Selected Text:</strong>
                <p>"{selectedText}"</p>
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter your comment for AI to understand what changes you want..."
                className="comment-input"
                rows={4}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowCommentModal(false)} className="cancel-button">
                Cancel
              </button>
              <button onClick={addAIComment} className="add-button">
                <Plus className="w-4 h-4" />
                Add Comment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Edit Modal */}
      {showManualModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Manual Edit</h3>
              <button onClick={() => setShowManualModal(false)} className="close-button">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="modal-content">
              <div className="selected-text-display">
                <strong>Selected Text:</strong>
                <p>"{selectedText}"</p>
              </div>
              <textarea
                value={manualEdit}
                onChange={(e) => setManualEdit(e.target.value)}
                placeholder="Enter the new text to replace the selected text..."
                className="edit-input"
                rows={4}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowManualModal(false)} className="cancel-button">
                Cancel
              </button>
              <button onClick={addManualEdit} className="add-button">
                <Check className="w-4 h-4" />
                Add Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalEditor; 